const wrapSelection = (value, start, end, before, after, placeholder = '') => {
  const selected = value.slice(start, end);
  const inner = selected || placeholder;
  const insertion = `${before}${inner}${after}`;
  const nextValue = `${value.slice(0, start)}${insertion}${value.slice(end)}`;

  if (selected) {
    return {
      value: nextValue,
      selectionStart: start,
      selectionEnd: start + insertion.length,
    };
  }

  return {
    value: nextValue,
    selectionStart: start + before.length,
    selectionEnd: start + before.length + inner.length,
  };
};

const prefixLines = (value, start, end, prefix) => {
  const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
  const nextNewline = value.indexOf('\n', end);
  const lineEnd = nextNewline === -1 ? value.length : nextNewline;
  const block = value.slice(lineStart, lineEnd);
  const prefixed = block
    .split('\n')
    .map((line) => (line.startsWith(prefix) ? line : `${prefix}${line}`))
    .join('\n');
  const nextValue = `${value.slice(0, lineStart)}${prefixed}${value.slice(lineEnd)}`;

  return {
    value: nextValue,
    selectionStart: lineStart,
    selectionEnd: lineStart + prefixed.length,
  };
};

export const applyMarkdownFormat = (value, start, end, format) => {
  const safeValue = value || '';
  const safeStart = Math.max(0, Math.min(start ?? safeValue.length, safeValue.length));
  const safeEnd = Math.max(safeStart, Math.min(end ?? safeStart, safeValue.length));

  switch (format) {
    case 'bold':
      return wrapSelection(safeValue, safeStart, safeEnd, '**', '**', 'текст');
    case 'italic':
      return wrapSelection(safeValue, safeStart, safeEnd, '*', '*', 'текст');
    case 'strike':
      return wrapSelection(safeValue, safeStart, safeEnd, '~~', '~~', 'текст');
    case 'code':
      return wrapSelection(safeValue, safeStart, safeEnd, '`', '`', 'код');
    case 'codeblock': {
      const selected = safeValue.slice(safeStart, safeEnd);
      const inner = selected || 'код';
      const insertion = `\`\`\`\n${inner}\n\`\`\``;
      const nextValue = `${safeValue.slice(0, safeStart)}${insertion}${safeValue.slice(safeEnd)}`;
      const contentStart = safeStart + 4;

      return {
        value: nextValue,
        selectionStart: contentStart,
        selectionEnd: contentStart + inner.length,
      };
    }
    case 'link': {
      const text = safeValue.slice(safeStart, safeEnd) || 'текст';
      const insertion = `[${text}](url)`;
      const nextValue = `${safeValue.slice(0, safeStart)}${insertion}${safeValue.slice(safeEnd)}`;
      const urlStart = safeStart + text.length + 3;

      return {
        value: nextValue,
        selectionStart: urlStart,
        selectionEnd: urlStart + 3,
      };
    }
    case 'h1':
      return prefixLines(safeValue, safeStart, safeEnd, '# ');
    case 'h2':
      return prefixLines(safeValue, safeStart, safeEnd, '## ');
    case 'h3':
      return prefixLines(safeValue, safeStart, safeEnd, '### ');
    case 'ul':
      return prefixLines(safeValue, safeStart, safeEnd, '- ');
    case 'ol':
      return prefixLines(safeValue, safeStart, safeEnd, '1. ');
    case 'quote':
      return prefixLines(safeValue, safeStart, safeEnd, '> ');
    default:
      return {
        value: safeValue,
        selectionStart: safeStart,
        selectionEnd: safeEnd,
      };
  }
};

export const insertMarkdownImage = (value, start, end, alt, url) => {
  const safeValue = value || '';
  const safeStart = Math.max(0, Math.min(start ?? safeValue.length, safeValue.length));
  const safeEnd = Math.max(safeStart, Math.min(end ?? safeStart, safeValue.length));
  const selected = safeValue.slice(safeStart, safeEnd).replace(/\s+/g, ' ').trim();
  const label = (selected || alt || 'картинка').replace(/[\[\]]/g, '');
  let insertion = `![${label}](${url})`;
  const before = safeValue.slice(0, safeStart);
  const after = safeValue.slice(safeEnd);

  if (before && !before.endsWith('\n')) {
    insertion = `\n\n${insertion}`;
  }
  if (!after.startsWith('\n')) {
    insertion = `${insertion}\n`;
  }

  const nextValue = `${before}${insertion}${after}`;
  const cursor = before.length + insertion.length;

  return {
    value: nextValue,
    selectionStart: cursor,
    selectionEnd: cursor,
  };
};

export const getTextAreaElement = (ref) => {
  const current = ref?.current;
  if (!current) {
    return null;
  }

  return current.resizableTextArea?.textArea
    || current.nativeElement
    || (current.tagName === 'TEXTAREA' ? current : null);
};
